<template>
    <div id="top" class="billing-detail-page claim-detail-page">
        <div class="claim-detail-intro">
            <UiPageHeader title="Billing Review" description="Review this billing batch and submit final claims when ready.">
                <template #actions>
                    <button type="button" class="claim-all-link" @click="handleCancel">
                        <ArrowLeft :size="17" aria-hidden="true" /><span>All Billing Batches</span>
                    </button>
                </template>
            </UiPageHeader>
        </div>
    <div class="sba-collection-page-surface is-workbench care-billing-view-surface">
        <div class="claim-view-container">

        <LoadingDiv v-if="!isReady" />

        <div class="claim-review-workbench sba-slide-in" v-else>

            <div v-if="loadError || error" class="card-panel red lighten-5" role="alert">
                <p>{{ loadError || error }}</p>
                <button v-if="loadError" type="button" class="btn teal" :disabled="isLoading" @click="loadCluster">
                    <i class="material-icons left">refresh</i>Retry
                </button>
            </div>

            <!-- Success -->
            <SubmitSummary :cluster="success" @cancel="handleCancel" v-if="success" />

            <!-- Main Displays  -->
            <div id="mid-top"></div>
            <template v-if="cluster && !loadError">
                <section class="claim-secondary-header">

                    <section class="claim-dossier-card">
                        <div class="claim-dossier-title">
                            <label class="active teal-text">Billing Review</label>
                            <h5>{{ clusterPeriod }}</h5>
                            <p>Review encounter items and complete this batch before submission.</p>
                            
                        </div>

                        <div class="claim-dossier-context">
                            <span class="context-pill">{{ String(cluster.scheme?.name ?? 'Scheme not recorded').toUpperCase() }}</span>
                            
                            <span class="context-pill" :class="clusterStatusClass">{{ clusterStatusLabel }}</span>
                            <span v-if="clusterPaymentStatus !== clusterStatusLabel" class="context-pill is-payment">{{ clusterPaymentStatus }}</span>
                        </div>
                    </section>

                    <section class="claim-dossier-metrics">
                        <div class="metric-cell">
                            <span>Sent Total</span>
                            <strong class="metric-relaxed-value">&#8358;{{ billingSentTotal }}</strong>
                        </div>
                        <div class="metric-cell">
                            <span>Encounters</span>
                            <strong class="metric-relaxed-value">{{ encounters.length }} {{ encounters.length === 1 ? "visit" : "visits" }}</strong>
                        </div>
                        <div class="metric-cell">
                            <span>Stage</span>
                            <strong class="metric-relaxed-value" :class="clusterStatusClass">{{ clusterStatusLabel }}</strong>
                        </div>
                        <div class="metric-cell metric-action-cell">
                            <button type="button" class="download-action" :class="{ 'is-disabled': !canDownloadPaymentAdvice, 'is-submitted-download': cluster.status === 'p' }"
                                :disabled="!canDownloadPaymentAdvice" @click.prevent="handleDownload()">
                                <i class="material-icons">download</i>
                                <span>Payment Advice</span>
                            </button>
                        </div>
                    </section>

                    <section class="claim-mode-strip">
                        <button type="button" class="claim-mode-close" title="Back to Billing Batches" aria-label="Back to Billing Batches" @click="handleCancel"><i class="material-icons" aria-hidden="true">close</i></button>
                        <div class="claim-mode-actions">
                            <button
                                v-for="tab in tabsState"
                                :key="tab.id"
                                type="button"
                                class="claim-mode-option"
                                :class="{ 'is-active': tab.id === currState?.id, 'is-disabled': isReviewTabDisabled(tab) }"
                                :disabled="isReviewTabDisabled(tab)"
                                @click="handleActiveState(tab)"
                            >
                                {{ tab.title }}
                                <span v-if="tab.title.toLowerCase().includes('summary')">({{ encounters.length ? encounters.length : 0 }})</span>
                            </button>
                        </div>
                    </section>
                </section>

                <section class="claim-secondary-body-frame">
                    <div class="progress" v-if="isLoading">
                        <div class="indeterminate"></div>
                    </div>

                    <div id="displays"></div>
                    <EnrolleeBill :cluster="cluster" :encounter="encounter" @back="handleEncounterBack"
                        @next="handleNext" @draft="handleDraft" @draft-clear="handleDraftClear"
                        @updated="handleEncounterUpdated" @removed="handleEncounterRemoved" v-if="next" />

                    <div v-else>
                        <SummaryBill :cluster="cluster" :encounters="encountersWithDraft" :isSubmitting="isSubmitting"
                            :isManual="true" @reload="handleReload" @cancel="handleCancel" />

                     <div class="care-submit-row" v-if="cluster.status === 'q'">
                            <button class="waves-effect waves-light btn-large care-add-enrollee-button"
                                v-if="canAddManualEncounter"
                                :class="{ disabled: isSubmitting }"
                                @click.prevent="handleAddEnrollee">
                                <i class="medium material-icons left">person_add</i>Add Enrollee
                            </button>

                            <button class="waves-effect waves-light btn-large btn modal-trigger care-submit-button"
                                :class="{
                                    disabled: isSubmitting || encountersWithDraft.find(x => x.status === 'q') || !encounters.length || lock,
                                }" data-target="confirmSubmit">
                                <i class="medium material-icons left">check_circle</i>{{ isSubmitting ? "Submit" :
                                    "Final Claims Submit"
                                }}
                            </button>
                            <!-- Confirmation -->
                            <Teleport to="body">
                                <Modal id="confirmSubmit" class="modal care-billing-confirm-modal" :title="alertTitle" :message="alertMessage"
                                    @confirm="handleConfirm" v-if="cluster" />
                            </Teleport>

                     </div>

                    </div>
                </section>
            </template>

        </div>

        </div>
    </div>


    </div>
</template>


<script>
import { ref, onMounted, onUpdated, computed, watchEffect, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ageSex, capWordsFx, } from "@/composables/fxLibrary/coreFx";
import getItemsOfItemsOfItems from "@/composables/getItemsOfItemsOfItems";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import { getCachedServerDate } from "@/composables/requestOptimization";
import { getCache, addCache, removeCache } from "@/composables/cache/cache";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import { ArrowLeft } from "@lucide/vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import EnrolleeBill from "@/components/billing/EnrolleeBillComponent.vue";
import { normalizeBillingEncounter, normalizeBillingCluster } from "@/components/billing/billingData";
import SummaryBill from "@/components/billing/SummaryBillComponent.vue";
import SubmitSummary from "@/components/claim/SubmitSummaryClaimComponent.vue";
import {
    grossTotalSentFx,
    totalSentFx,
    totalApprovedFx,
    grossTotalApprovedFx,
    checkPaidFx,
    paymentStatusFx,
} from "@/composables/calFunction/cal";
import { downloadExcelFx } from "@/composables/reportFunction/downloader";
import { getFullMonthName } from "@/composables/fxLibrary/months";
import Modal from "@/components/alerts/ModalComponent.vue";
import { HMO } from '@/laravel/config';
import nativeUi from "@/ui/native-ui";

export default {
    name: "claimsAdd",
    props: ["clusterId", "active"],
    emits: ["menu", "active", "cancel"],
    components: { UiPageHeader, ArrowLeft, LoadingDiv, EnrolleeBill, SummaryBill, SubmitSummary, Modal },
    setup(props, context) {
        const lock = ref(true);
        const isManual = ref(false); // toggle manual claims
        const adder = ref(true);
        const serverTime = ref(null);
        const isReady = ref(false);
        const isLoading = ref(false);
        const isSubmitting = ref(false);
        const cluster = ref(null);
        const encounter = ref(null);
        const next = ref(false);
        const error = ref(null);
        const loadError = ref(null);
        const success = ref(null);
        const notifyTitle = ref(null);
        const notifyMessage = ref(null);
        const alertTitle = ref("Confirm");
        const alertMessage = ref("Are you sure you want to submit? Please confirm that all the details provided are accurate. Once submitted, changes cannot be made unless approved by your HMO.");
        const home = ref(null);
        const currTab = ref(null);
        const tabs = ref([]);
        const tabsState = ref([]);
        const currState = ref(null);

        // vue router
        const router = useRouter();

        // store
        const store = useStore();

        // config
        const providerId = store.state?.user?.account?.id;
        const billingDraftKey = "provider-billing-draft";
        const isManualScheme = (scheme) => {
            return scheme?.is_manual === true || scheme?.is_manual === 1 || scheme?.is_manual === "1";
        };
        const hasManualBillingSchemes = computed(() => {
            return (store.state?.user?.schemes || []).some(isManualScheme);
        });
        const canAddManualEncounter = computed(() => {
            return cluster.value?.status === "q" && isManualScheme(cluster.value?.scheme);
        });
        const syncEncounterInCluster = (value) => {
            if (!cluster.value || !value?.id) {
                return null;
            }

            const updatedEncounter = normalizeBillingEncounter(value);
            const currentEncounters = cluster.value.encounters || [];
            const exists = currentEncounters.some(
                item => Number(item.id) === Number(updatedEncounter.id),
            );

            if (exists) {
                const encounterIndex = currentEncounters.findIndex(
                    item => Number(item.id) === Number(updatedEncounter.id),
                );
                currentEncounters[encounterIndex] = updatedEncounter;
            } else {
                currentEncounters.push(updatedEncounter);
            }

            if (encounter.value && Number(encounter.value.id) === Number(updatedEncounter.id)) {
                encounter.value = updatedEncounter;
            }

            return updatedEncounter;
        };
        const isValidBillingDraft = (draft) => {
            return draft
                && draft._draft === true
                && cluster.value
                && cluster.value.status === "q"
                && Number(draft.cluster_id) === Number(cluster.value.id)
                && Number(draft.provider_id) === Number(cluster.value.provider_id)
                && Number(draft.scheme_id) === Number(cluster.value.scheme_id)
                && !!draft.diagnosis;
        };
        const billingDraft = computed(() => {
            const draft = getCache(billingDraftKey);

            if (!draft || !cluster.value) {
                return null;
            }

            if (!isValidBillingDraft(draft)) {
                return null;
            }

            return draft;
        });
        const isReviewTab = (tab) => {
            return tab?.title?.toLowerCase().includes("review");
        };
        const isReviewTabDisabled = (tab) => {
            return isReviewTab(tab) && !next.value;
        };

        // start
        onMounted(() => {
            // page name
            document.title = "View Claims";
        });

        // menu
        onMounted(() => {
            // load tab
            tabs.value.push({ id: 1, name: "Billing", title: "Home", icon: "arrow_back", url: "billing", hash: null, });
            tabs.value.push({ id: 2, name: "Billing", title: "Review", icon: null, url: null, hash: null });

            if (hasManualBillingSchemes.value) {
                tabs.value.push({ id: 3, name: "Billing", title: "Start", icon: "add", url: "billing", hash: "#batch-start" });
            }

            // set default
            currTab.value = tabs.value.find(x => x.title.includes("Review"));
            // set home
            home.value = tabs.value.find(x => x.title.includes("Review"));
        });

        let loadSequence = 0;
        const loadCluster = async () => {
            const sequence = ++loadSequence;
            error.value = null;
            loadError.value = null;
            isReady.value = false;
            isLoading.value = true;
            try {
                const { res, err } = await getItemsOfItemsOfItems("cluster/provider", providerId, props.clusterId);
                if (sequence !== loadSequence) return;
                if (!res.value) throw err.value || new Error("The billing batch could not be loaded.");
                const clusterData = normalizeBillingCluster(res.value.data);
                const monthYear = clusterData.month_year.split("_");
                clusterData.month = { search: Number(monthYear[0]), name: getFullMonthName(monthYear[0] - 1) };
                clusterData.year = { search: Number(monthYear[1]), name: monthYear[1] };
                cluster.value = clusterData;

                const draft = getCache(billingDraftKey);
                if (draft && !isValidBillingDraft(draft)) {
                    removeCache(billingDraftKey);
                }
            } catch (cause) {
                if (sequence !== loadSequence) return;
                loadError.value = cause?.message || "The billing batch could not be loaded. Please retry.";
            } finally {
                if (sequence === loadSequence) {
                    isReady.value = true;
                    isLoading.value = false;
                }
            }
        };

        watch(() => props.clusterId, () => {
            cluster.value = null;
            encounter.value = null;
            next.value = false;
            currState.value = tabsState.value[0] || null;
            success.value = null;
            isReady.value = false;
            loadCluster();
        }, { immediate: true });

        watch(
            () => store.state.realtimeAuthEvent?.event_id,
            (eventId) => {
                const event = store.state.realtimeAuthEvent;
                const matchesCluster = cluster.value?.encounters?.some(item => Number(item.id) === Number(event?.encounter_id));

                if (!eventId || !matchesCluster || encounter.value || billingDraft.value) {
                    return;
                }

                isLoading.value = true;
                loadCluster();
                store.commit("dismissRealtimeAuthNotice", eventId);
            }
        );


        // handle cancel
        const resetBillingScroll = (behavior = "auto") => {
            nextTick(() => {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        window.scrollTo({
                            top: 0,
                            behavior,
                        });
                    });
                });
            });
        };

        const handleCancel = () => {
            // clear temp save
            router.push({ name: "billing" }).then(() => {
                resetBillingScroll();
            });
        }

        // menu
        onMounted(() => {
            (async () => {
                const { res } = await getCachedServerDate(getItems);

                if (res.value) {
                    // current time
                    serverTime.value = res.value.data;
                }

            })();
        });

        // watch serverTime.value
        watchEffect(() => {
            if (serverTime.value) {
                // if (store.state.cluster) {
                if (Number(serverTime.value?.current_month) > Number(cluster.value?.month_year?.split("_")[0]) || Number(serverTime.value?.current_year) > Number(cluster.value?.month_year?.split("_")[1])) {
                    // open lock
                    lock.value = false;
                }
                // }
            }
        })

        // start
        onMounted(() => {
            // load tab
            tabsState.value.push({ id: 1, title: "Summary (All)" });
            tabsState.value.push({ id: 2, title: "Review" });
            // set default
            currState.value = tabsState.value[0];
            // send menu to parent
            context.emit("menu", tabsState.value);
        });

        // change state
        const handleState = (val) => {

            // clear all
            handleClear();

            // set
            if (val) {
                currState.value = val;
                router.push({ hash: "#top" });
            }

        }

        // close notification
        const handleOk = () => {
            // clear notification
            notifyTitle.value = null;
            notifyMessage.value = null;
            // Restore scrollbar (insurance)
            resetScrollBar();
        }

        // more
        onUpdated(() => {
            var elems = document.querySelectorAll(".dropdown-trigger");
            nativeUi.Dropdown.init(elems, {});
        });

        // alert
        onMounted(() => {
            // modal
            const elems = document.querySelectorAll(".modal");
            nativeUi.Modal.init(elems, {});
        });

        // alert
        onUpdated(() => {
            // modal
            const elems = document.querySelectorAll(".modal");
            nativeUi.Modal.init(elems, {});
        });

        //from functions
        const capWords = (val) => {
            return capWordsFx(val);
        };

        // ageSex
        const showAgeSex = (dob, sex) => {
            const { fx } = ageSex(dob, sex);
            return fx.value;
        };

        const billingSentTotal = computed(() => {
            return grossTotalSentFx(encounters.value);
        });

        const clusterPeriod = computed(() => {
            if (!cluster.value?.month?.name || !cluster.value?.year?.name) {
                return "Claims period";
            }

            return `${cluster.value.month.name} ${cluster.value.year.name}`;
        });

        const clusterStatusLabel = computed(() => {
            if (cluster.value?.status === "q") {
                return "Unsubmitted";
            }

            if (cluster.value?.status === "p") {
                return "Submitted";
            }

            if (cluster.value?.status === "v" || cluster.value?.status === "r") {
                return "Completed";
            }

            return "In Review";
        });

        const clusterStatusClass = computed(() => {
            if (cluster.value?.status === "q") {
                return "is-unvetted";
            }

            if (cluster.value?.status === "p") {
                return "is-submitted";
            }

            return "is-vetted";
        });

        const clusterPaymentStatus = computed(() => {
            if (!cluster.value) {
                return "Payment status";
            }

            if (cluster.value.status === "r" || cluster.value.status === "v") {
                return checkPaidFx(cluster.value) ? "Paid" : "Pending";
            }

            return paymentStatusFx(cluster.value);
        });

        const canDownloadPaymentAdvice = computed(() => {
            return cluster.value?.status !== "q" && !billingDraft.value && encounters.value.length > 0;
        });

        // scrollbar
        const resetScrollBar = () => {
            // Restore scrollbar (insurance)
            document.body.style.overflow = 'auto';
        }

        // clear all
        const handleClear = () => {
            error.value = null;
            success.value = null;
        }

        // handle curr status
        const handleActive = (tab) => {
            currTab.value = tab;
            // refer
            if (tab.hash) {
                router.push({ name: tab.url, hash: tab.hash });
            } else {
                router.push({ name: tab.url });
            }
        };

        // handle curr status
        const handleActiveState = (tab) => {
            if (isReviewTabDisabled(tab)) {
                return;
            }

            // handle tab
            if (tab) {
                currState.value = tab;
                // send back
                // to parent menu
                context.emit("active", tab);
            }
            // top
            if (currState.value?.title.toLowerCase().includes("summary")) {
                // open summary
                handleReview("view");
            } else if (currState.value?.title.toLowerCase().includes("review")) {
                // route display
                router.push({ hash: "#displays" });
            }
        };

        // monitor active
        watch(() => props.active, (newVal) => {
            if (newVal) {
                handleActiveState(newVal);
            }
        });

        // handle next
        const handleNext = async (val) => {

            // clear all 
            // notifications
            handleClear();

            // start
            if (cluster.value && val?.encounter) {

                if (val.encounter?._draft) {
                    handleDraftClear();
                    return;
                }

                if (val.submit) {
                    handleDraftClear();
                }

                syncEncounterInCluster(val.encounter);
                cluster.value.encounters = cluster.value.encounters.filter(x => x.status !== "d");
                // save payload
                // store.commit("loadCluster", cluster.value);

            }

            // if submit
            if (val.submit) {
                // close encounter
                encounter.value = null;
                // close enrolleeClaim
                next.value = false;
                // set default
                currState.value = tabsState.value?.find(x => x.title.toLowerCase().includes("summary"));
                // scroll up
                router.push({ hash: "#displays" });
            }

        };

        // handle reload
        const handleReload = (val) => {
            if (val.encounter?._draft) {
                if (!isValidBillingDraft(val.encounter)) {
                    handleDraftClear();
                    return;
                }
            }

            encounter.value = normalizeBillingEncounter(val.encounter);
            // editId.value = val.encounter.editId;
            next.value = true;
            // set default
            currState.value = tabsState.value?.find(x => x.title.toLowerCase().includes("review"));
            // to parent menu
            context.emit("active", currState.value);
            // scroll up
            router.push({ hash: "#top" });
        };

        const handleEncounterUpdated = (updatedEncounter) => {
            syncEncounterInCluster(updatedEncounter);
        };

        const handleEncounterBack = async (val) => {
            if (val?.encounter?.id && val.encounter.encounter_date_set && !val.encounter._draft) {
                syncEncounterInCluster(val.encounter);
            }

            await handleReview("view");
        };

        const handleEncounterRemoved = (val) => {
            if (!cluster.value || !val?.encounterId) {
                return;
            }

            cluster.value.encounters = (cluster.value.encounters || []).filter(
                item => Number(item.id) !== Number(val.encounterId),
            );
            handleDraftClear();
            encounter.value = null;
            next.value = false;
            currState.value = tabsState.value?.find(x => x.title.toLowerCase().includes("summary"));
            context.emit("active", currState.value);
            router.push({ hash: "#displays" });
        };

        // handle add enrollee
        const handleAddEnrollee = () => {
            if (!canAddManualEncounter.value || isSubmitting.value) {
                return;
            }

            const draft = getCache(billingDraftKey);

            if (draft && Number(draft.cluster_id) !== Number(cluster.value?.id)) {
                removeCache(billingDraftKey);
                encounter.value = null;
            } else if (isValidBillingDraft(draft)) {
                encounter.value = draft;
            } else {
                removeCache(billingDraftKey);
                encounter.value = null;
            }

            next.value = true;
            currState.value = tabsState.value?.find(x => x.title.toLowerCase().includes("review"));
            context.emit("active", currState.value);
            router.push({ hash: "#displays" });
        };

        // handle draft
        const handleDraft = (draft) => {
            if (!draft || !cluster.value || cluster.value.status !== "q" || !draft.diagnosis) {
                return;
            }

            const normalizedDraft = {
                ...draft,
                id: draft.id || `draft-${cluster.value.id}`,
                _draft: true,
                cluster_id: cluster.value.id,
                encounter_cluster_id: cluster.value.id,
                provider_id: cluster.value.provider_id,
                scheme_id: cluster.value.scheme_id,
                scheme: cluster.value.scheme,
                status: "q",
                updated_at: new Date().toISOString(),
            };

            addCache(billingDraftKey, normalizedDraft);
        };

        // clear draft
        const handleDraftClear = () => {
            removeCache(billingDraftKey);
        };

        // handle review
        const handleReview = async (val) => {
            if (val == "view") {
                next.value = false;
                // reset default
                currState.value = tabsState.value[0];
                // to parent menu
                context.emit("active", currState.value);
                // Wait for the summary table to replace the encounter view before scrolling.
                await nextTick();
                await router.push({ hash: "#billing-summary-list" });
            } else if (val == "add") {
                handleAddEnrollee();
            }
        };

        // download
        const handleDownload = () => {
            if (!canDownloadPaymentAdvice.value) {
                return;
            }

            const data = [];

            encounters.value.forEach((x, y) => {
                x.claims.forEach((z, j) => {
                    const row = {
                        No: j === 0 ? y + 1 : "",
                        Name: j === 0 ? x.name : "",
                        ID: j === 0 ? x.enrollee_number : "",
                        Encounter: j === 0 ? x.encounter_date : "",
                        End: j === 0 ? x.encounter_date_end : "",
                        Diagnosis: j === 0 ? x.diagnosis : "",
                        Item: z.item,
                        sent_qty: z.sent_qty,
                        appr_qty: x.status == "d" || z.status == "d" ? 0 : z.approved_qty,
                        Sent: z.sent_price,
                        Approved: x.status == "d" || z.status == "d" ? 0 : z.approved_price,
                        Comment: z.comment,
                    };

                    data.push(row);
                });

                data.push({
                    No: "",
                    Name: "",
                    ID: "",
                    Encounter: "",
                    End: "",
                    Diagnosis: "",
                    Item: "",
                    sent_qty: "",
                    appr_qty: "",
                    Sent: totalSentFx(x),
                    Approved: totalApprovedFx(x),
                    Comment: "",
                });

                data.push({});
            });

            data.push({});
            data.push({
                No: "",
                Name: "",
                ID: "",
                Encounter: "",
                End: "",
                Diagnosis: "",
                Item: "",
                sent_qty: "",
                appr_qty: "Final",
                Sent: grossTotalSentFx(encounters.value),
                Approved: grossTotalApprovedFx(encounters.value),
                Comment: "",
            });

            return downloadExcelFx(data, `${cluster.value.provider?.name || "Provider"}_${cluster.value.scheme?.name || "Scheme"}_${clusterPeriod.value} Claims`);
        }

        // confirmation
        const handleConfirm = (val) => {
            // call submit if confirmed
            if (val) {
                // p = submitted
                handleSubmit("p");
            } else {
                // Restore scrollbar (insurance)
                resetScrollBar();
            }
        }

        // handle review
        const handleSubmit = async (clusterStatusVal) => {

            // check first
            if (!encounters.value.find(x => x.status === 'q') && !billingDraft.value) {

                // clear all 
                // notifications
                handleClear();

                // start
                isSubmitting.value = true;
                // clear error
                error.value = null;

                // go
                const { res } = await putItem("cluster", cluster.value.id, {
                    status: clusterStatusVal,
                });


                if (res.value) {
                    if (res.value.data.status !== "q") {
                        handleDraftClear();
                    }

                    // next action
                    if (res.value.data.status === "q") {

                        // nothing, just back home
                        handleState("c"); // c=current, u=user, h=hmo, n=null

                        // clear
                        cluster.value = null;


                    } else {

                        if (res.value.data.status === "p") {
                            // load success cluster
                            // res.value.data.month = cluster.value.month.name;
                            // res.value.data.year = cluster.value.year.name;
                            // res.value.data.scheme = cluster.value.scheme;

                            //load cluster
                            cluster.value.status = res.value.data.status;

                            // filter
                            cluster.value.encounters.filter(x => x.status !== "d");

                            // save payload
                            //   store.commit("loadCluster", cluster.value);

                            nativeUi.toast({
                                html: "Final claims submitted successfully <i class='material-icons'>check_circle</i>",
                                displayLength: 5000,
                                inDuration: 300,
                                outDuration: 300,
                            });

                            // reset
                            handleState("c"); // c=current, u=user, h=hmo, n=null

                        } else {
                            // nothing, just back home
                            handleState("c"); // c=current, u=user, h=hmo, n=null
                        }

                    }

                } else {
                    error.value = "Sorry, Something went wrong!";
                }

                // Restore scrollbar (insurance)
                resetScrollBar();

                // end
                isSubmitting.value = false; // in case of incasecity!

            }

        };

        // output claims results
        const encounters = computed(() => {
            if (!cluster.value || !("encounters" in cluster.value)) {
                return [];
            } else {
                return cluster.value.encounters;
            }
        });

        const encountersWithDraft = computed(() => {
            if (!billingDraft.value) {
                return encounters.value;
            }

            return [...encounters.value, billingDraft.value];
        });

        return {
            loadCluster,
            lock,
            isManual,
            adder,
            handleCancel,
            handleActive,
            handleActiveState,
            handleDownload,
            home,
            currTab,
            tabs,
            tabsState,
            currState,
            capWords,
            showAgeSex,
            billingSentTotal,
            clusterPeriod,
            clusterStatusLabel,
            clusterStatusClass,
            clusterPaymentStatus,
            canDownloadPaymentAdvice,
            handleOk,
            handleClear,
            handleState,
            handleConfirm,
            handleReview,
            handleAddEnrollee,
            handleDraft,
            handleDraftClear,
            handleNext,
            handleReload,
            handleEncounterUpdated,
            handleEncounterBack,
            handleEncounterRemoved,
            handleSubmit,
            notifyTitle,
            notifyMessage,
            alertTitle,
            alertMessage,
            success,
            error,
            loadError,
            isReady,
            isLoading,
            isSubmitting,
            next,
            cluster,
            encounter,
            encounters,
            encountersWithDraft,
            canAddManualEncounter,
            isReviewTabDisabled,
            HMO: HMO,
        };
    },
};
</script>

<style scoped>
@layer components {
  .billing-detail-page :deep(:is(.billing-back-link, .billing-back-action, .billing-member-close, .claim-all-link, .claim-mode-close)),
  .billing-detail-page :deep(:is(.billing-back-link, .billing-back-action, .billing-member-close, .claim-all-link, .claim-mode-close):is(:hover, :focus, :active)),
  .billing-detail-page :deep(:is(.billing-back-link, .billing-back-action, .billing-member-close, .claim-all-link, .claim-mode-close) *) { text-decoration: none !important; }
}

.claim-detail-page {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2rem 2rem 3rem;
  width: 100%;
}

.claim-detail-intro {
  border-bottom: 1px solid var(--ui-border);
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
}

.claim-detail-intro :deep(.ui-page-header) {
  margin-bottom: 0;
}

.claim-detail-intro :deep(.ui-page-header__title) {
  font-size: clamp(1.75rem, 2.1vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.claim-detail-intro :deep(.ui-page-header__description) {
  font-size: 1rem;
  line-height: 1.55;
}

.claim-all-link {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-primary-foreground);
  cursor: pointer;
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 0.9rem;
}

.claim-all-link:hover {
  filter: brightness(0.95);
}

.claim-all-link:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.claim-detail-page > .sba-collection-page-surface {
  background: transparent;
  border: 0;
  min-height: 0;
  padding-bottom: 0;
}

.claim-view-container {
  margin: 0;
  max-width: none;
  padding: 0;
  width: 100%;
}

.claim-review-workbench {
  padding-bottom: 0;
  padding-top: 0;
  width: 100%;
}

.claim-dossier-card,
.claim-dossier-metrics,
.claim-mode-strip,
.claim-secondary-body-frame,
.claim-review-workbench :deep(.vetting-soft-core),
.claim-review-workbench :deep(.vetting-core-card),
.claim-review-workbench :deep(.vetting-member-card),
.claim-review-workbench :deep(.vetting-diagnosis-card),
.claim-review-workbench :deep(.vetting-claims-card),
.claim-review-workbench :deep(.vetting-edit-card) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.claim-secondary-header {
  box-shadow: none;
  margin-bottom: 0;
  overflow: visible;
  position: relative;
}

.claim-secondary-body-frame {
  background: #ffffff;
  border: 1px solid var(--ui-border);
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  box-sizing: border-box;
  margin-top: 14px;
  padding: 16px;
  position: relative;
}

.claim-dossier-card,
.claim-dossier-metrics,
.claim-mode-strip {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
}

.claim-dossier-card {
  align-items: center;
  border-color: var(--ui-border);
  border-radius: 6px 6px 0 0;
  box-shadow: none;
  display: flex;
  gap: 22px;
  justify-content: space-between;
  padding: 26px 34px;
  position: relative;
}

.claim-dossier-title {
  background: transparent;
  border-radius: 0;
  flex: 0 1 58%;
  max-width: 58%;
  min-width: 0;
  padding: 0;
}

.claim-dossier-title label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.claim-dossier-title h5 {
  color: #263238;
  font-size: 1.25rem;
  font-weight: 650;
  line-height: 1.22;
  margin: 2px 0 0;
  overflow-wrap: anywhere;
}

.claim-dossier-title p {
  color: #607d8b;
  font-size: 0.94rem;
  line-height: 1.55;
  margin: 7px 0 0;
  max-width: 760px;
}

.claim-dossier-context {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 430px;
}

.context-pill {
  background: #fafafa;
  border: 1px solid #e1e8eb;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.context-pill.is-unvetted {
  background: #fff4d6;
  border-color: #f1c75b;
  color: #795000;
}

.context-pill.is-vetted {
  background: #e0f2f1;
  border-color: #80cbc4;
  color: #00695c;
}

.context-pill.is-payment {
  background: #eceff1;
  color: #263238;
}

.context-pill.is-related {
  text-transform: uppercase;
}

.context-pill.is-related.is-supplementary {
  background: #e8f5e9;
  border-color: #a5d6a7;
  color: #2e7d32;
}

.context-pill.is-related.is-reconciliation {
  background: #e3f2fd;
  border-color: #90caf9;
  color: #1565c0;
}

.claim-dossier-metrics {
  border-color: var(--ui-border);
  border-top: 0;
  border-radius: 0;
  box-shadow: none;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 0;
}

.metric-cell {
  border-left: 1px solid #e1e8eb;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-height: 76px;
  padding: 17px 22px;
}

.metric-cell:first-child {
  border-left: 0;
}

.metric-cell span {
  color: #607d8b;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.metric-cell strong {
  color: #263238;
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.35;
}

.metric-cell strong.metric-relaxed-value {
  color: #37474f;
  font-size: 0.98rem;
  font-weight: 650;
}

.metric-action-cell {
  align-items: flex-end;
  justify-content: center;
}

.download-action {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 8px;
  min-height: 38px;
  padding: 0 13px;
}

.download-action i {
  color: #00897b;
  font-size: 19px;
}

.claim-mode-strip {
  align-items: center;
  background: #ffffff;
  border-color: var(--ui-border);
  border-top: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 0;
  padding: 10px 16px;
}

.claim-mode-actions {
  background: #ffffff;
  border: 1px solid var(--ui-border);
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  margin-left: auto;
  padding: 5px;
}

.claim-mode-option {
  background: #f8fafb;
  border: 1px solid var(--ui-border);
  border-radius: 5px;
  box-shadow: 0 8px 16px -18px rgba(38, 50, 56, 0.8);
  color: #263238;
  cursor: pointer;
  flex: 0 0 auto;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0;
  min-height: 38px;
  padding: 0 16px;
  text-align: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
}

.claim-mode-option:first-child {
  border-left: 1px solid var(--ui-border);
}

.claim-mode-option:hover {
  background: #eef7f7;
  border-color: #80cbc4;
  box-shadow: 0 10px 18px -18px rgba(0, 105, 92, 0.55);
  color: #00695c;
}

.claim-mode-option.is-active {
  background: #263238;
  border-color: #263238;
  box-shadow: 0 10px 18px -14px rgba(38, 50, 56, 0.9);
  color: #ffffff;
}

.claim-mode-close {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 5px;
  color: #c62828;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 38px;
  justify-content: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
  width: 42px;
}

.claim-mode-close:hover,
.claim-mode-close:focus {
  background: #fff7f7;
  border-color: #ef9a9a;
  box-shadow: 0 10px 18px -18px rgba(198, 40, 40, 0.55);
  color: #b71c1c;
}

.claim-mode-close i {
  font-size: 20px;
  line-height: 1;
}

.claim-secondary-body-frame .progress {
  margin: 0 0 14px;
}

.claim-secondary-body-frame :deep(.section) {
  padding-bottom: 0;
  padding-top: 0;
}

.claim-secondary-body-frame :deep(.card),
.claim-secondary-body-frame :deep(.card-panel) {
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  box-shadow: none;
}

.claim-secondary-body-frame :deep(.card) {
  overflow: hidden;
}

.claim-secondary-body-frame :deep(.grey.lighten-4),
.claim-secondary-body-frame :deep(.grey.lighten-5) {
  background-color: #f8fafb !important;
}

.claim-secondary-body-frame :deep(.teal.lighten-5) {
  background-color: #e8f6f6 !important;
}

.claim-secondary-body-frame :deep(.yellow.lighten-1) {
  background-color: #fff176 !important;
  border-radius: 4px;
}

.claim-secondary-body-frame :deep(.collection) {
  border-color: #d7e1e5;
  border-radius: 6px;
  overflow: hidden;
}

.claim-secondary-body-frame :deep(.collection-item) {
  border-bottom-color: #e1e8eb;
}

.claim-secondary-body-frame :deep(.btn),
.claim-secondary-body-frame :deep(.btn-large),
.claim-secondary-body-frame :deep(.chip) {
  letter-spacing: 0;
}

@media only screen and (max-width: 992px) {
  .claim-view-container {
    width: 96%;
  }

  .claim-dossier-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .claim-dossier-title {
    max-width: none;
    width: 100%;
  }

  .claim-dossier-context {
    justify-content: flex-start;
    max-width: none;
    min-width: 0;
    width: 100%;
  }

  .claim-dossier-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .metric-cell:nth-child(3) {
    border-left: 0;
    border-top: 1px solid #e1e8eb;
  }

  .metric-cell:nth-child(4) {
    border-top: 1px solid #e1e8eb;
  }

  .claim-mode-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .claim-mode-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .claim-secondary-body-frame.is-vetting {
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 8px 0 0;
  }

  .claim-secondary-body-frame.is-vetting .claim-vetting-surface {
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
  }
}

@media only screen and (min-width: 701px) and (max-width: 992px) {

  .claim-dossier-card,
  .claim-mode-strip {
    padding: 22px;
  }

  .claim-mode-actions {
    flex-wrap: nowrap;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .claim-mode-option {
    flex: 0 0 auto;
  }

  .claim-secondary-body-frame {
    padding: 18px;
  }

  .claim-secondary-body-frame.is-vetting {
    padding: 8px 0 0;
  }
}

@media only screen and (max-width: 700px) {
  .claim-view-container {
    width: calc(100% - 16px);
  }

  .claim-dossier-card {
    padding: 22px;
  }

  .claim-dossier-title h5 {
    font-size: 1.2rem;
  }

  .claim-dossier-title p {
    font-size: 0.94rem;
  }

  .claim-dossier-context {
    gap: 6px;
  }

  .claim-dossier-metrics {
    grid-template-columns: 1fr;
  }

  .metric-cell,
  .metric-cell:nth-child(3),
  .metric-cell:nth-child(4) {
    border-left: 0;
    border-top: 1px solid #e1e8eb;
  }

  .metric-cell:first-child {
    border-top: 0;
  }

  .metric-action-cell {
    align-items: flex-start;
  }

  .download-action {
    justify-content: center;
    width: 100%;
  }

  .claim-mode-actions {
    display: flex;
    flex-wrap: nowrap;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
  }

  .claim-mode-actions::-webkit-scrollbar {
    display: none;
  }

  .claim-mode-option {
    flex: 0 0 auto;
  }

  .claim-secondary-body-frame {
    padding: 12px;
  }

  .claim-secondary-body-frame.is-vetting {
    border-radius: 0;
    margin-top: 10px;
    padding: 6px 0 0;
  }

  .claim-secondary-body-frame.is-vetting .claim-vetting-tertiary {
    padding-left: 6px;
    padding-right: 6px;
  }

  .claim-secondary-body-frame.is-vetting .claim-vetting-tertiary-link {
    padding-left: 14px;
    padding-right: 14px;
  }

  .claim-secondary-body-frame :deep(.card-content),
  .claim-secondary-body-frame :deep(.card-panel) {
    padding: 16px;
  }

  .claim-secondary-body-frame :deep(.collection-item) {
    line-height: 1.45;
    padding: 12px 14px;
  }
}

@media only screen and (max-width: 992px) {
  .claim-detail-page {
    padding-inline: 1.25rem;
  }
}

@media only screen and (max-width: 600px) {
  .claim-detail-page {
    padding: 1.25rem 1rem 2.5rem;
  }

  .claim-detail-intro {
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
  }

  .claim-all-link {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .claim-dossier-card,
  .claim-dossier-metrics,
  .claim-mode-strip,
  .claim-secondary-body-frame,
  .claim-review-workbench :deep(.vetting-soft-core),
  .claim-review-workbench :deep(.vetting-core-card),
  .claim-review-workbench :deep(.vetting-member-card),
  .claim-review-workbench :deep(.vetting-diagnosis-card),
  .claim-review-workbench :deep(.vetting-claims-card),
  .claim-review-workbench :deep(.vetting-edit-card) {
    animation: none;
  }
}
@layer components {

    .billing-detail-page .care-submit-row { background: transparent !important; border: 0 !important; border-top: 1px solid var(--ui-border) !important; display: flex !important; flex-wrap: wrap !important; gap: .75rem !important; justify-content: flex-end !important; margin-top: 1.5rem !important; padding: 1.25rem !important; }
    .billing-detail-page .care-submit-row > button { border-radius: calc(var(--ui-radius) - 2px) !important; box-shadow: none !important; font-size: .8125rem !important; font-weight: 600 !important; height: 2.75rem !important; line-height: 2.75rem !important; margin: 0 !important; padding: 0 1rem !important; }

:global(.care-billing-confirm-modal) {
    left: 50% !important;
    max-width: 720px;
    position: fixed !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: min(720px, calc(100vw - 32px));
    z-index: 10060 !important;
}
}
.claim-mode-option:disabled, .download-action:disabled { cursor: not-allowed; opacity: .45; }
.context-pill.is-submitted { background: #eef5ff; border-color: #c5dafa; color: #2563a6; }
@media (max-width: 600px) { .care-submit-row > button { width: 100%; } }
</style>
